import React from 'react';
import {
  Table, TableHead, TableBody, TableRow, TableCell, TextField,
  Select, MenuItem, IconButton, Box, Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import type { Actividad, Labor, Consumidor, SaveRendimientoDetalle } from '../types/rendimiento';

interface Props {
  detalles: SaveRendimientoDetalle[];
  actividades: Actividad[];
  labores: Labor[];
  consumidoresSeleccionados: Consumidor[];
  trabajadorNames: Record<number, string>;
  horasAsistencia: Record<number, { normal: number; extras25: number; extras35: number }>;
  onDetalleChange: (index: number, field: string, value: any) => void;
  onProduccionChange: (detalleIdx: number, idConsumidor: number, cantidad: number) => void;
  onDeleteDetalle: (index: number) => void;
}

const RendimientoDetalleGrid: React.FC<Props> = ({
  detalles, actividades, labores, consumidoresSeleccionados, trabajadorNames,
  horasAsistencia, onDetalleChange, onProduccionChange, onDeleteDetalle,
}) => {
  const getLaboresByActividad = (idActividad: number) =>
    labores.filter(l => l.idActividad === idActividad);

  const getProduccionCantidad = (det: SaveRendimientoDetalle, idConsumidor: number) => {
    const prod = det.producciones.find(p => p.idConsumidor === idConsumidor);
    return prod?.cantidad || 0;
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 900 }} translate="no">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold', width: 40 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 180 }}>Trabajador</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Actividad</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 150 }}>Labor</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 70 }}>U.Med</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 60 }}>T.Asist</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 70, bgcolor: '#e8f5e9' }} align="right">H.Norm</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 65, bgcolor: '#fff3e0' }} align="right">HE 25%</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 65, bgcolor: '#fce4ec' }} align="right">HE 35%</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 90 }} align="right">Rend.S/</TableCell>
            {consumidoresSeleccionados.map(c => (
              <TableCell key={c.idConsumidor} sx={{ fontWeight: 'bold', width: 90, bgcolor: '#e3f2fd' }} align="center">
                {c.descripcion}
              </TableCell>
            ))}
            <TableCell sx={{ width: 40 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {detalles.length === 0 && (
            <TableRow>
              <TableCell colSpan={10 + consumidoresSeleccionados.length + 1} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Sin registros. Use "Agregar Trabajador" para comenzar.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {detalles.map((det, idx) => {
            const prevWorker = idx > 0 ? detalles[idx - 1].idTrabajador : null;
            const isFirstOfWorker = det.idTrabajador !== prevWorker;
            let span = 0;
            if (isFirstOfWorker) {
              for (let j = idx; j < detalles.length && detalles[j].idTrabajador === det.idTrabajador; j++) span++;
            }

            return (
            <TableRow key={idx} sx={idx % 2 === 1 ? { bgcolor: '#fafafa' } : {}}>
              <TableCell>{det.item}</TableCell>
              {isFirstOfWorker && (
                <TableCell rowSpan={span} sx={{ fontWeight: 600, fontSize: 12, verticalAlign: 'middle', borderRight: '1px solid #e0e0e0' }}>
                  {trabajadorNames[det.idTrabajador] || `ID: ${det.idTrabajador}`}
                </TableCell>
              )}
              <TableCell>
                <Select size="small" fullWidth value={det.idActividad || ''}
                  onChange={e => onDetalleChange(idx, 'idActividad', e.target.value)}>
                  {actividades.filter(a => a.activo).map(a => (
                    <MenuItem key={a.idActividad} value={a.idActividad}>
                      {a.codigo} - {a.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell>
                <Select size="small" fullWidth value={det.idLabor || ''}
                  onChange={e => onDetalleChange(idx, 'idLabor', e.target.value)}>
                  {getLaboresByActividad(det.idActividad).filter(l => l.activo).map(l => (
                    <MenuItem key={l.idLabor} value={l.idLabor}>
                      {l.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell>
                {labores.find(l => l.idLabor === det.idLabor)?.unidadMedida || 'HORAS'}
              </TableCell>
              <TableCell>
                <Select size="small" fullWidth value={det.tipoAsistencia}
                  onChange={e => onDetalleChange(idx, 'tipoAsistencia', e.target.value)}>
                  <MenuItem value="R">R</MenuItem>
                  <MenuItem value="A">A</MenuItem>
                </Select>
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: '#e8f5e9' }}>
                {horasAsistencia[det.idTrabajador]?.normal ?? det.cantidadTotal ?? 0}
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: '#fff3e0' }}>
                {horasAsistencia[det.idTrabajador]?.extras25 ?? 0}
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: '#fce4ec' }}>
                {horasAsistencia[det.idTrabajador]?.extras35 ?? 0}
              </TableCell>
              <TableCell>
                <TextField size="small" type="number" value={det.rendimientoSoles}
                  onChange={e => onDetalleChange(idx, 'rendimientoSoles', parseFloat(e.target.value) || 0)}
                  inputProps={{ step: 0.01, style: { textAlign: 'right' } }}
                  sx={{ width: 90 }} />
              </TableCell>
              {consumidoresSeleccionados.map(c => (
                <TableCell key={c.idConsumidor}>
                  <TextField size="small" type="number"
                    value={getProduccionCantidad(det, c.idConsumidor) || ''}
                    onChange={e => onProduccionChange(idx, c.idConsumidor, parseFloat(e.target.value) || 0)}
                    inputProps={{ step: 0.01, style: { textAlign: 'right' } }}
                    sx={{ width: 80 }} />
                </TableCell>
              ))}
              <TableCell>
                <IconButton size="small" color="error" onClick={() => onDeleteDetalle(idx)}>
                  <Delete fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default RendimientoDetalleGrid;
